/**
 * A period of usage, read whole: this period and the one before it analysed,
 * and the findings drawn from the two — what both Statistics pages render. And
 * the wire shapes the desktop app and the account area exchange days in.
 */
import { analyse, type Analysis } from './analysis';
import { daysBetween, shiftDay, type UsageDayRow } from './day';
import { findings, type Finding, type FindingsWhere } from './findings';

export const USAGE_RANGES = [7, 30, 90] as const;
export type UsageRange = (typeof USAGE_RANGES)[number];

export interface UsageReport {
  from: string;
  to: string;
  /** The period it is compared with, of the same length, just before. */
  prevFrom: string;
  prevTo: string;
  current: Analysis;
  previous: Analysis;
  findings: Finding[];
}

/** The window a range covers, ending today, and the window of the same length before it. */
export function rangeOf(today: string, days: number): { from: string; to: string; prevFrom: string; prevTo: string } {
  const from = shiftDay(today, -(days - 1));
  return { from, to: today, prevFrom: shiftDay(from, -days), prevTo: shiftDay(from, -1) };
}

/** `rows` may hold any days; only the two windows are read. */
export function buildReport(
  rows: readonly UsageDayRow[],
  today: string,
  days: number,
  where: FindingsWhere = 'app'
): UsageReport {
  const r = rangeOf(today, days);
  const current = analyse(rows, r.from, r.to, daysBetween(r.from, r.to));
  const previous = analyse(rows, r.prevFrom, r.prevTo, daysBetween(r.prevFrom, r.prevTo));
  return {
    from: r.from,
    to: r.to,
    prevFrom: r.prevFrom,
    prevTo: r.prevTo,
    current,
    previous,
    findings: findings({ current, previous, previousLabel: `the previous ${days} days`, where })
  };
}

/* ── the wire ─────────────────────────────────────────────────────────────── */

/** How many days one upload may carry — a backlog larger than this goes in several. */
export const MAX_UPLOAD_DAYS = 120;

/**
 * `POST /api/cloud/orgs/:org/usage` — the days that changed since the last
 * upload, each **whole**: the account area replaces its row for that member,
 * machine and day with what arrives, so sending a day twice is the same as
 * once. The machine is the device token the install calls with — nothing more
 * travels to say which one it is.
 */
export interface UsageUpload {
  days: UsageDayRow[];
}

/** One member's days, as the account area's page reads them. */
export interface MemberUsage {
  email: string;
  name: string | null;
  /** Every machine's days added together, one row per day. */
  days: UsageDayRow[];
  /** How many of the member's installs sent a day in the window read. */
  machines: number;
  /** When the last day arrived from any of them, ISO, or null. */
  lastSent: string | null;
}

/** `GET /api/orgs/:org/usage?from=&to=` — the caller's own days, or every member's for an owner or admin. */
export interface OrgUsageAnswer {
  from: string;
  to: string;
  /** Whether the caller sees the whole organization (owner, admin) or only themselves. */
  everyone: boolean;
  members: MemberUsage[];
  /** Whether the organization collects (`usageStatsEnabled` published on), and whether it holds that answer. */
  collects: boolean;
  held: boolean;
  /** The settings version that turned collection on as it stands, and when it was published. */
  version: number;
  publishedAt: string | null;
  /** For an owner or an administrator: members whose installs sent nothing in the window, by e-mail. */
  silent: string[];
}
